import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import { formValueSelector } from 'redux-form';
import PriceBookApi from './Api';
import {
  ENTITY_PRICEBOOK_INDUSTRIES,
  priceBookIndustriesSchema,
  ENTITY_PRICEBOOK_INDUSTRIES_LIST,
  priceBookIndustriesListSchema,
  ENTITY_PRICEBOOK_CATEGORIES_LIST,
  priceBookCategoriesListSchema,
  ENTITY_PRICEBOOK_CATEGORIES,
  ENTITY_PRICEBOOK_MATERIALS_LIST,
  ENTITY_PRICEBOOK_MATERIALS,
  priceBookCategoriesSchema,
  priceBookMaterialsSchema,
  priceBookMaterialsListSchema,
  ENTITY_PRICEBOOK_SERVICES_LIST,
  priceBookServicesListSchema,
  ENTITY_PRICEBOOK_SERVICES,
  priceBookServicesSchema,
  ENTITY_MATERIAL_CATEGORIES,
  materialCategoriesSchema,
  ENTITY_MATERIAL_CATEGORIES_LIST,
  materialCategoriesListSchema,
  materialBreadcrumbSchemaList,
  ENTITY_MATERIAL_BREADCRUMBS_LIST,
  taxRateSchemaList,
  ENTITY_TAX_RATES_LIST,
  laborRateSchemaList,
  ENTITY_LABOR_RATES_LIST,
  materialMarkupSchemaList,
  ENTITY_MATERIAL_MARKUPS_LIST,
  ENTITY_PRICING_CONTENT_BOOKS,
  ENTITY_PRICING_CONTENT_BOOKS_LIST,
  pricingContentBooksListSchema,
  ENTITY_PRICING_CONTENT_BOOKS_WITH_STATUS,
  ENTITY_PRICING_CONTENT_BOOKS_WITH_STATUS_LIST,
  pricingContentBooksWithStatusListSchema,
  ENTITY_PRICING_CONTENT_UPDATES_VIEWED,
} from '../../Schema/PriceBookSchema';
import { getEntities } from '../EntityData';
import { EmptyArray, EmptyObject } from '../EmptyDefaults';
import { isEntityPresentSelector } from '../../components/Common/Data/DataFetcherEntitiesHelper';
import { getCompositeSchemaKey } from '../../components/Common/Data/DataFetcherSuperUtils';
import { centsToDollars } from '../../components/HelperModules/CurrencyConverter';

export const BREAD_CRUMB_NAMESPACE = 'breadCrumb';
export const MATERIALS_BREAD_CRUMB_NAMESPACE = 'materialsBreadCrumb';
export const TOP_LEVEL_NAMESPACE = 'topLevel';

export const CREATE_OR_EDIT_SERVICE_FORM = 'CreateOrEditServiceForm';
export const serviceFormSelector = formValueSelector(CREATE_OR_EDIT_SERVICE_FORM);
export const SERVICE_LABOR_RATES = 'service_labor_rates';
export const MANAGE_TAX_RATES_FORM = 'manage_tax_rates_form';
export const taxRatesFormSelector = formValueSelector(MANAGE_TAX_RATES_FORM);
export const DEFAULT_TAX_RATE = { name: '', rate: 0 };
export const MANAGE_LABOR_RATES_FORM = 'manage_labor_rates_form';
export const laborRatesFormSelector = formValueSelector(MANAGE_LABOR_RATES_FORM);
export const DEFAULT_LABOR_RATE = { name: '', hourly_cost: 0, hourly_price: 0, default: false };
export const MANAGE_MATERIAL_MARKUPS_FORM = 'manage_material_markups_form';
export const materialMarkupsFormSelector = formValueSelector(MANAGE_MATERIAL_MARKUPS_FORM);
export const DEFAULT_SERVICE_MATERIAL = {
  quantity: 1,
  material: { cost: 0, price: 0 },
};

export const DEFAULT_SERVICE_LABOR_RATE = {
  number_of_hours: 1,
  labor_rate: { uuid: '', hourly_cost: 0, hourly_price: 0 },
};

export const DEFAULT_FILTERS = {
  Pro: false,
  'Profit Rhino': false,
};

export const SEARCH_SERVICES = 'services';
export const SEARCH_MATERIALS = 'materials';
export const SEARCH_CATEGORIES = 'service_categories';
export const SEARCH_MATERIAL_CATEGORIES = 'material_categories';
export const SEARCH_DEBOUNCE_TIME = 500;
export const SEARCH_ROWS_PER_PAGE = 25;
export const DEFAULT_SEARCH_RESPONSE = { data: [], page: 1, total_count: 0 };

const servicesTableData = {
  tableTitle: 'Services',
  urlPrefix: 'services',
  columns: [
    {
      columnTitle: 'Service',
      propertyName: 'name',
      highlight: true,
      width: '15%',
    },
    {
      columnTitle: 'Description',
      propertyName: 'description',
      highlight: true,
      width: '35%',
    },
    {
      columnTitle: 'Task code',
      propertyName: 'task_number',
      highlight: true,
      width: '10%',
    },
    {
      columnTitle: 'Cost',
      propertyName: 'cost',
      isAmount: true,
      width: '10%',
    },
    {
      columnTitle: 'Price',
      propertyName: 'price',
      isAmount: true,
      width: '10%',
    },
  ],
};

const categoriesTableData = {
  tableTitle: 'Service Categories',
  urlPrefix: 'categories',
  columns: [
    {
      columnTitle: 'Category',
      propertyName: 'name',
      highlight: true,
      width: '80%',
    },
  ],
};

const materialCategoriesTableData = {
  tableTitle: 'Material Categories',
  urlPrefix: 'material_categories',
  columns: [
    {
      columnTitle: 'Category',
      propertyName: 'name',
      highlight: true,
      width: '80%',
    },
  ],
};

const materialsTableData = {
  tableTitle: 'Materials',
  urlPrefix: 'materials',
  columns: [
    {
      columnTitle: 'Material',
      propertyName: 'name',
      highlight: true,
      width: '15%',
    },
    {
      columnTitle: 'Description',
      propertyName: 'description',
      highlight: true,
      width: '35%',
    },
    {
      columnTitle: 'Part number',
      propertyName: 'part_number',
      highlight: true,
      width: '10%',
    },
    {
      columnTitle: 'Cost',
      propertyName: 'cost',
      isAmount: true,
      width: '10%',
    },
    {
      columnTitle: 'Price',
      propertyName: 'price',
      isAmount: true,
      width: '10%',
    },
  ],
};

export const paginatedTableData = {
  [SEARCH_SERVICES]: servicesTableData,
  [SEARCH_MATERIALS]: materialsTableData,
  [SEARCH_CATEGORIES]: categoriesTableData,
  [SEARCH_MATERIAL_CATEGORIES]: materialCategoriesTableData,
};

export const BULK_PRICING_ROOT = '_root';

export const CATEGORIES_PER_PAGE = 100;
export const SERVICES_PER_PAGE = 100;
export const MATERIALS_PER_PAGE = 100;
export const MATERIAL_CATEGORIES_PER_PAGE = 100;

export const DRAWER_WIDTH = 275;
export const NEW_DRAWER_WIDTH = 260;
export const TOP_TOOLBAR_HEIGHT = 137;

export const formatAvailableCategories = (availableCategories) => {
  if (availableCategories.length === 0) {
    return [{ uuid: undefined, name: 'No categories found in this industry - pick another industry' }];
  }
  return availableCategories.map(({ uuid, bread_crumb_info }) => {
    const breadCrumbInfo = bread_crumb_info.info.slice(1);
    let name;
    if (breadCrumbInfo.length === 1) {
      name = breadCrumbInfo[0].name;
    } else {
      name = breadCrumbInfo
        .map((category) => category.name)
        .reduce((accumulator, currentValue) => `${accumulator} > ${currentValue}`);
    }
    return { uuid, name };
  });
};

export const formatAvailableMaterialCategories = (categories) =>
  categories?.map(({ uuid, bread_crumb_info }) => {
    const breadCrumbInfo = bread_crumb_info?.info || [];
    let name;
    if (breadCrumbInfo.length === 1) {
      name = breadCrumbInfo[0].name;
    } else {
      name = breadCrumbInfo
        .map((category) => category.name)
        .reduce((accumulator, currentValue) => `${accumulator} > ${currentValue}`, '');
    }
    return { uuid, name };
  });

export const getBreadCrumbInfo = createSelector(
  (state) => state[BREAD_CRUMB_NAMESPACE] || {},
  (breadCrumb) => breadCrumb.info || EmptyArray
);

export const getMaterialBreadCrumbInfo = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_MATERIAL_BREADCRUMBS_LIST]?.bread_crumb_info) {
    return denormalize('bread_crumb_info', materialBreadcrumbSchemaList, entities).info;
  }
  return EmptyArray;
});

export const PRICEBOOK_INDUSTRY_OBJECT = 'pricebook_industry';
export const PRICEBOOK_CATEGORY_OBJECT = 'category';

const INDUSTRIES = 'industries';

export const getPriceBookIndustries = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_PRICEBOOK_INDUSTRIES_LIST]?.list) {
    const industries = denormalize('list', priceBookIndustriesListSchema, entities).data;
    return industries.sort((industry1, industry2) => industry1.order_index - industry2.order_index);
  }
  return EmptyArray;
});

export const getPricingContentBooks = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_PRICING_CONTENT_BOOKS_LIST]?.list) {
    const books = denormalize('list', pricingContentBooksListSchema, entities).data;
    return books;
  }
  return EmptyArray;
});

export const getPricingContentBooksWithStatus = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_PRICING_CONTENT_BOOKS_WITH_STATUS_LIST]?.list) {
    const books_with_status = denormalize('list', pricingContentBooksWithStatusListSchema, entities).data;
    return books_with_status;
  }
  return EmptyArray;
});

export const getPriceBookIndustry = createSelector(
  getEntities,
  (_, params) => params.uuid,
  (entities, uuid) => {
    if (entities[ENTITY_PRICEBOOK_INDUSTRIES]) {
      return denormalize(uuid, priceBookIndustriesSchema, entities);
    }
    return EmptyObject;
  }
);

export const getPriceBookCategories = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_PRICEBOOK_CATEGORIES_LIST,
      key: getCompositeSchemaKey(paginationData),
    }),
  (entities, schemaKey, isCategoriesListPresent) => {
    if (!isCategoriesListPresent) return EmptyObject;
    const categoriesObject = denormalize(schemaKey, priceBookCategoriesListSchema, entities);
    categoriesObject.data?.sort((category1, category2) => category1.order_index - category2.order_index);
    return categoriesObject || EmptyObject;
  }
);

export const getPriceBookCategory = createSelector(
  getEntities,
  (_, params) => params.uuid,
  (entities, uuid) => {
    if (entities[ENTITY_PRICEBOOK_CATEGORIES]) {
      return denormalize(uuid, priceBookCategoriesSchema, entities) || EmptyObject;
    }
    return EmptyObject;
  }
);

export const getPriceBookServices = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_PRICEBOOK_SERVICES_LIST,
      key: getCompositeSchemaKey(paginationData),
    }),
  (entities, schemaKey, isServicesListPresent) => {
    if (!isServicesListPresent) return EmptyObject;
    const servicesObject = denormalize(schemaKey, priceBookServicesListSchema, entities);
    servicesObject.data?.sort((service1, service2) => service1.order_index - service2.order_index);
    return servicesObject || EmptyObject;
  }
);

export const getPriceBookService = createSelector(
  getEntities,
  (_, params) => params.uuid,
  (entities, uuid) => {
    if (entities[ENTITY_PRICEBOOK_SERVICES]) {
      return denormalize(uuid, priceBookServicesSchema, entities) || EmptyObject;
    }
    return EmptyObject;
  }
);

export const getLineItemFromService = createSelector(
  getPriceBookService,
  (_, { quantity = 0 } = {}) => quantity,
  (
    {
      price = 0,
      cost = 0,
      name,
      description,
      task_number,
      uuid,
      taxable = false,
      image,
      flat_rate_enabled,
      content_source_uuid,
      content_source,
      content_version,
    },
    quantity
  ) => ({
    amount: quantity * price,
    description,
    task_number,
    id: parseInt(Math.random() * 1000, 10),
    is_existing: true,
    kind: 'labor',
    name,
    quantity,
    qty_in_hundredths: quantity * 100,
    service_item_id: uuid,
    service_item_type: 'OrganizationalLineItemTemplate',
    taxable,
    unit_cost: cost,
    unit_price: price,
    image_url: image,
    flat_rate_enabled,
    content_source_uuid,
    content_source,
    content_version,
  })
);

const getPriceBookMaterials = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_PRICEBOOK_MATERIALS_LIST,
      key: getCompositeSchemaKey(paginationData),
    }),
  (entities, schemaKey, isMaterialsListPresent) => {
    if (!isMaterialsListPresent) return EmptyArray;
    const materialsObject = denormalize(schemaKey, priceBookMaterialsListSchema, entities);
    materialsObject.data.sort((material1, material2) => material1.order_index - material2.order_index);
    return materialsObject;
  }
);

export const getPriceBookMaterial = createSelector(
  getEntities,
  (_state, { uuid } = {}) => uuid,
  (state, { uuid } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_PRICEBOOK_MATERIALS,
      key: uuid,
    }),
  (entities, uuid, isMaterialPresent) => {
    if (!isMaterialPresent) return EmptyObject;
    return denormalize(uuid, priceBookMaterialsSchema, entities);
  }
);

export const getLineItemFromMaterial = createSelector(
  getPriceBookMaterial,
  (_, { quantity = 0 } = {}) => quantity,
  (
    {
      price = 0,
      cost = 0,
      name,
      description,
      part_number,
      uuid,
      taxable = false,
      image,
      flat_rate_enabled,
      content_source_uuid,
      content_source,
      content_version,
    },
    quantity
  ) => ({
    amount: quantity * price,
    description,
    id: parseInt(Math.random() * 1000, 10),
    is_existing: true,
    kind: 'materials',
    name,
    quantity,
    part_number,
    qty_in_hundredths: quantity * 100,
    service_item_id: uuid,
    service_item_type: 'Pricebook::Material',
    taxable,
    unit_cost: cost,
    unit_price: price,
    image_url: image,
    flat_rate_enabled,
    content_source_uuid,
    content_source,
    content_version,
  })
);

const getMaterialCategories = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_MATERIAL_CATEGORIES_LIST,
      key: getCompositeSchemaKey(paginationData),
    }),
  (entities, schemaKey, isMaterialCategoriesListPresent) => {
    if (!isMaterialCategoriesListPresent) return EmptyArray;
    const categoriesObject = denormalize(schemaKey, materialCategoriesListSchema, entities);
    categoriesObject.data.sort((category1, category2) => category1.order_index - category2.order_index);
    return categoriesObject;
  }
);

export const getMaterialCategory = createSelector(
  getEntities,
  (_state, { uuid } = {}) => uuid,
  (state, { uuid } = {}) =>
    isEntityPresentSelector(state, {
      namespace: ENTITY_MATERIAL_CATEGORIES,
      key: uuid,
    }),
  (entities, uuid, isMaterialCategoryPresent) => {
    if (!isMaterialCategoryPresent) return EmptyObject;
    return denormalize(uuid, materialCategoriesSchema, entities);
  }
);

export const getTaxRates = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_TAX_RATES_LIST]?.list) {
    const taxRates = denormalize('list', taxRateSchemaList, entities).data;
    return taxRates.map((tax) => ({ ...tax, rate: (tax.rate * 100).toFixed(3), _destroy: false }));
  }
  return EmptyArray;
});

export const getLaborRates = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_LABOR_RATES_LIST]?.list) {
    const laborRates = denormalize('list', laborRateSchemaList, entities).data;
    return laborRates.map((labor) => ({
      ...labor,
      hourly_cost: labor.hourly_cost,
      hourly_price: labor.hourly_price,
      _destroy: false,
    }));
  }
  return EmptyArray;
});

export const getLaborRatesInDollar = createSelector(getLaborRates, (laborRates) =>
  laborRates.map((labor) => ({
    ...labor,
    hourly_cost: centsToDollars(labor.hourly_cost),
    hourly_price: centsToDollars(labor.hourly_price),
  }))
);

const getMaterialMarkups = createSelector(getEntities, (entities) => {
  if (entities[ENTITY_MATERIAL_MARKUPS_LIST]?.list) {
    const materialMarkups = denormalize('list', materialMarkupSchemaList, entities).data.sort(
      (markup1, markup2) => markup1.lower_bound - markup2.lower_bound
    );
    return materialMarkups.map((markup) => ({
      ...markup,
      markup_percentage: parseFloat((Number(markup.percentage) * 100).toFixed(2)),
      profit_percentage: parseFloat(((Number(markup.percentage) / (1 + Number(markup.percentage))) * 100).toFixed(2)),
      _destroy: false,
    }));
  }
  return EmptyArray;
});

export const getPricingContentNotificationsViewed = createSelector(getEntities, (entities) => {
  return entities[ENTITY_PRICING_CONTENT_UPDATES_VIEWED];
});

export const fetchPriceBookIndustries = {
  fetchingApiFunc: PriceBookApi.fetchPriceBookIndustries,
  selector: getPriceBookIndustries,
  schema: priceBookIndustriesListSchema,
};

export const fetchPricingContentBooks = {
  fetchingApiFunc: PriceBookApi.fetchPricingContentBooks,
  selector: getPricingContentBooks,
  schema: pricingContentBooksListSchema,
};

export const fetchPricingContentBooksWithStatus = (organization_uuid) => ({
  fetchingApiFunc: PriceBookApi.fetchPricingContentBooksWithStatus,
  selector: getPricingContentBooksWithStatus,
  schema: pricingContentBooksWithStatusListSchema,
  paramsFromProps: () => [{ organization_uuid: organization_uuid }],
  alwaysFetch: true,
});

export const fetchPriceBookCategories = {
  fetchingApiFunc: PriceBookApi.fetchPriceBookCategories,
  selector: getPriceBookCategories,
  schema: priceBookCategoriesListSchema,
  paramsFromProps: ({ paginationData, parent_uuid, pricebook_industry_uuid, filters }) => [
    {
      parent_uuid,
      pricebook_industry_uuid,
      ...paginationData,
      filters,
    },
  ],
};

export const fetchPriceBookServices = {
  fetchingApiFunc: PriceBookApi.fetchServices,
  selector: getPriceBookServices,
  schema: priceBookServicesListSchema,
  paramsFromProps: ({ paginationData, pricebook_category_uuid, managed_by, expand }) => [
    { pricebook_category_uuid, managed_by, expand, ...paginationData },
  ],
  alwaysFetch: true,
};

export const fetchPriceBookMaterials = {
  fetchingApiFunc: PriceBookApi.fetchMaterials,
  selector: getPriceBookMaterials,
  schema: priceBookMaterialsListSchema,
  paramsFromProps: ({ uuid, paginationData, expand } = {}) => [{ material_category_uuid: uuid, expand, ...paginationData }],
  alwaysFetch: true,
};

export const fetchPriceBookService = {
  fetchingApiFunc: PriceBookApi.fetchPriceBookService,
  selector: getPriceBookService,
  schema: priceBookServicesSchema,
  paramsFromProps: ({uuid, expand} = {}) => [{ expand }, uuid],
  alwaysFetch: true,
};

export const fetchPriceBookMaterial = {
  fetchingApiFunc: PriceBookApi.fetchPriceBookMaterial,
  selector: getPriceBookMaterial,
  schema: priceBookMaterialsSchema,
  paramsFromProps: ({uuid, expand} = {}) => [{ expand }, uuid],
  alwaysFetch: true,
};

export const fetchMaterialCategory = {
  fetchingApiFunc: PriceBookApi.fetchMaterialCategory,
  selector: getMaterialCategory,
  schema: materialCategoriesSchema,
  paramsFromProps: (props) => [props.uuid],
};

export const fetchCategory = {
  fetchingApiFunc: PriceBookApi.fetchPriceBookCategory,
  selector: getPriceBookCategory,
  schema: priceBookCategoriesSchema,
  paramsFromProps: (props) => [props.uuid],
};

export const fetchParent = (url) => {
  if (url.includes(INDUSTRIES)) {
    return {
      fetchingApiFunc: PriceBookApi.fetchPriceBookIndustry,
      selector: getPriceBookIndustry,
      schema: priceBookIndustriesSchema,
      paramsFromProps: (props) => [props.uuid],
    };
  }
  return fetchCategory;
};

export const fetchBreadCrumbInfo = {
  fetchingApiFunc: PriceBookApi.fetchBreadCrumbInfo,
  selector: getBreadCrumbInfo,
  dataNamespace: BREAD_CRUMB_NAMESPACE,
  paramsFromProps: (props) => [props.uuid],
  alwaysFetch: true,
};

export const fetchMaterialBreadCrumbInfo = {
  fetchingApiFunc: PriceBookApi.fetchMaterialBreadCrumbInfo,
  selector: getMaterialBreadCrumbInfo,
  schema: materialBreadcrumbSchemaList,
  paramsFromProps: (props) => [props.uuid],
  alwaysFetch: true,
};

export const fetchMaterialCategories = {
  fetchingApiFunc: PriceBookApi.getMaterialCategories,
  selector: getMaterialCategories,
  schema: materialCategoriesListSchema,
  paramsFromProps: ({ uuid, paginationData } = {}) => [{ parent_uuid: uuid, ...paginationData }],
};

export const fetchLeavesMaterialCategories = {
  fetchingApiFunc: PriceBookApi.getLeavesMaterialCategories,
  selector: getMaterialCategories,
  schema: materialCategoriesListSchema,
  paramsFromProps: ({ uuid, paginationData } = {}) => [{ parent_uuid: uuid, ...paginationData }],
};

export const fetchTaxRates = {
  fetchingApiFunc: PriceBookApi.fetchTaxRates,
  selector: getTaxRates,
  schema: taxRateSchemaList,
  paramsFromProps: ({ paginationData } = {}) => [{ ...paginationData }],
};

export const fetchLaborRates = {
  fetchingApiFunc: PriceBookApi.fetchLaborRates,
  selector: getLaborRates,
  schema: laborRateSchemaList,
  paramsFromProps: ({ paginationData } = {}) => [{ ...paginationData }],
};

export const fetchMaterialMarkups = {
  fetchingApiFunc: PriceBookApi.fetchMaterialMarkups,
  selector: getMaterialMarkups,
  schema: materialMarkupSchemaList,
  paramsFromProps: ({ paginationData } = {}) => [{ ...paginationData }],
};
